/**
 * RecordEntryGrid — the ONE entry table behind all three production-record
 * inputs: the chef's Temperature logs, the school's Transport logs, and the
 * school's Service records (PROD-1279 D3/D4).
 *
 * It generalises the chef's hand-rolled CSS grid (`components/tasks/
 * location-temp-form.tsx`, which now renders through here) into columns +
 * rows + sub-rows. A column is a `count` box or a `temp` control; a ROW owns
 * its temp cells and each row/sub-row owns its count cells, because the three
 * tables all need one reading spread over several count lines — a service
 * item measured once but counted per grade band, a milk drop measured once
 * but counted per SKU.
 *
 * GEOMETRY. CSS grid cannot span a cell across sibling `<li>`s, so a row is a
 * FLEX PAIR: a lines block (`md:flex-1`) holding one grid line per row/sub-row,
 * and a temp block (`md:shrink-0`) centred beside the whole thing. With zero
 * count columns that reproduces the chef's
 * `md:grid-cols-[minmax(0,1fr)_13rem_13rem] md:gap-4` exactly, and it gives the
 * temp cell a real vertical span over the sub-rows for free.
 *
 * The two grid templates are built from the columns at runtime, so they travel
 * as CSS VARIABLES read by two literal Tailwind classes. Never interpolate a
 * template into a class name here: a class string assembled at runtime is
 * invisible to Tailwind's scanner and the column would silently collapse.
 *
 * Below `md` every row stacks: name + chip + legend on one line, count boxes in
 * a two-column grid under their own mini-labels, the temp control full width,
 * sub-rows as indented blocks. Layout is pure CSS breakpoints — no JS branching.
 */

import { cn } from '@ordo/ui/cn'
import { Skeleton } from '@ordo/ui/skeleton'
import type { CSSProperties, ReactNode } from 'react'

import type { TemperatureReadingWire, TemperatureSubmissionWire } from '@/api/cafeteria-temperatures'
import { CafeteriaModeTip } from '@/components/cafeteria/cafeteria-mode-tip'
import { CountInput } from '@/components/cafeteria/count-input'
import {
  NO_READING_LABEL,
  TEMP_CLASS_CHIP,
  TEMP_CLASS_LABEL,
  TEMPERATURE_RANGE_HINT,
  type TemperatureClass,
} from '@/components/cafeteria/temperature-table'
import { type TempCheckpointClass, TempCheckpointInput } from '@/components/tasks/temp-checkpoint-input'
import { formatTemperatureF, temperatureCellInvalid } from '@/lib/cafeteria/temperature-edit'

export const RECORD_ENTRY_SKELETON_ROWS = 6

/** Marks a cell a photo extraction filled at low confidence (PROD-1279 E5). Presentation only. */
export const RECORD_ENTRY_CHECK_HINT = 'Check this value. The photo was hard to read.'

export interface RecordEntryColumn {
  key: string
  /** Title Case. The desktop header cell AND the mobile mini-label — never CSS-uppercased. */
  header: string
  kind: 'count' | 'temp'
  /** Desktop track width, e.g. `'13rem'`. The grid templates are built from these. */
  width: string
  /** Completes each control's accessible name: `${row.label} ${inputLabel}`. */
  inputLabel: string
  /** Rides beside the desktop header only (the Non-reimbursable Info icon); never in the mini-label. */
  headerAdornment?: ReactNode
}

interface RecordEntryCellBase {
  /**
   * A value a photo extraction filled but could not read confidently: rings
   * the control amber and explains itself on hover. The flow clears it as soon
   * as the worker edits that cell.
   */
  flag?: 'check'
  disabled?: boolean
}

export interface RecordEntryCountCell extends RecordEntryCellBase {
  kind: 'count'
  value: number
  /** Digits-only text after a keystroke, a keypad press or a paste; `''` when emptied. */
  onValueChange: (next: string) => void
  /** Provenance mark rendered BEFORE the box (the cafeteria's `counted 38`). */
  tip?: ReactNode
}

export interface RecordEntryTempCell extends RecordEntryCellBase {
  kind: 'temp'
  /** False → the `N/A — room temp` pill. Callers pass `tempCheckRequired(class)`. */
  offered: boolean
  value: number | null
  onChange: (next: number | null) => void
  /** What cafeteria mode logged here — the Info tip beside the control. */
  reading?: TemperatureReadingWire | null
  /** What Production Records already holds; non-null locks the cell behind a Lock tip. */
  submitted?: TemperatureSubmissionWire | null
}

export type RecordEntryCell = RecordEntryCountCell | RecordEntryTempCell

export interface RecordEntrySubRow {
  key: string
  /** The muted, indented line in the item column: a grade band, a milk SKU. */
  legend: string
  /** COUNT cells only — the temperature belongs to the parent row. */
  cells: Record<string, RecordEntryCell>
  /** Full-width node under this line (a row's violations + its Reset link). */
  footer?: ReactNode
}

export interface RecordEntryRow {
  key: string
  label: string
  legend?: string | null
  /** Drives BOTH the HACCP chip and the temperature presets. */
  temperatureClass: TemperatureClass | null
  /** False = presets without a chip — the chef's rows carry the class but show no chip (D4). */
  chip?: boolean
  cells: Record<string, RecordEntryCell>
  subRows?: RecordEntrySubRow[]
  footer?: ReactNode
}

export interface RecordEntrySection {
  key: string
  /** `<MealtimeHeader/>` (chef) or `<RecordEntryCardHeader/>` (schools). */
  header: ReactNode
  rows: RecordEntryRow[]
  /** Rendered INSIDE the card after the rows (the write-ins footer). */
  footer?: ReactNode
  emptyText?: string
}

export interface RecordEntryGridProps {
  columns: ReadonlyArray<RecordEntryColumn>
  sections: ReadonlyArray<RecordEntrySection>
  loading?: boolean
}

/** The lines block's desktop tracks: the item column plus every COUNT column. */
export function recordEntryLinesTemplate(columns: ReadonlyArray<RecordEntryColumn>): string {
  return ['minmax(0,1fr)', ...columns.filter((c) => c.kind === 'count').map((c) => c.width)].join(' ')
}

/** The temp block's desktop tracks; `''` when the table takes no reading. */
export function recordEntryTempTemplate(columns: ReadonlyArray<RecordEntryColumn>): string {
  return columns
    .filter((c) => c.kind === 'temp')
    .map((c) => c.width)
    .join(' ')
}

/**
 * The schools' card header — the chef's `MealtimeHeader` band minus the meal
 * count and the serve-day badges, so the three tables share one implementation.
 * Label only: the mocks show no count, and PROD-1342 removed invented summaries.
 */
export function RecordEntryCardHeader({ label, note }: { label: string; note?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-t-[10px] border-b border-border bg-card px-4 py-3 text-sm font-medium">
      <span className="text-foreground">{label}</span>
      {note}
    </div>
  )
}

export function RecordEntryGrid({ columns, sections, loading = false }: RecordEntryGridProps) {
  const countColumns = columns.filter((c) => c.kind === 'count')
  const tempColumns = columns.filter((c) => c.kind === 'temp')
  const templates = {
    '--record-grid-cols': recordEntryLinesTemplate(columns),
    '--record-temp-cols': recordEntryTempTemplate(columns),
  } as CSSProperties

  return (
    <section className="space-y-3">
      {sections.map((section) => (
        <div key={section.key} className="overflow-hidden rounded-[10px] border" style={templates}>
          {section.header}

          {/* Column headers — desktop only, Title Case, no `uppercase` class. */}
          <div className="hidden border-b px-4 py-2 text-xs font-medium text-muted-foreground md:flex md:items-center md:gap-4">
            <div className="grid min-w-0 flex-1 grid-cols-[var(--record-grid-cols)] items-center gap-4">
              <span>Item</span>
              {countColumns.map((column) => (
                <span key={column.key} className="inline-flex items-center gap-1 whitespace-nowrap">
                  {column.header}
                  {column.headerAdornment}
                </span>
              ))}
            </div>
            {tempColumns.length > 0 ? (
              <div className="grid shrink-0 grid-cols-[var(--record-temp-cols)] items-center gap-4">
                {tempColumns.map((column) => (
                  <span key={column.key}>{column.header}</span>
                ))}
              </div>
            ) : null}
          </div>

          <ul className="divide-y" aria-busy={loading || undefined}>
            {loading ? (
              <SkeletonRows columnCount={columns.length} />
            ) : section.rows.length === 0 ? (
              <li className="px-4 py-8 text-center text-sm text-muted-foreground">{section.emptyText}</li>
            ) : (
              section.rows.map((row) => (
                <RowView key={row.key} row={row} countColumns={countColumns} tempColumns={tempColumns} />
              ))
            )}
          </ul>

          {section.footer}
        </div>
      ))}
    </section>
  )
}

function RowView({
  row,
  countColumns,
  tempColumns,
}: {
  row: RecordEntryRow
  countColumns: ReadonlyArray<RecordEntryColumn>
  tempColumns: ReadonlyArray<RecordEntryColumn>
}) {
  const showChip = row.chip !== false && row.temperatureClass !== null

  return (
    <li className="px-3 py-3 md:flex md:items-stretch md:gap-4 md:px-4">
      <div className="min-w-0 md:flex-1">
        <div className="md:grid md:grid-cols-[var(--record-grid-cols)] md:items-center md:gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="text-sm font-medium md:font-normal">{row.label}</span>
              {showChip && row.temperatureClass ? (
                <span
                  className={cn(
                    'shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium',
                    TEMP_CLASS_CHIP[row.temperatureClass],
                  )}>
                  {TEMP_CLASS_LABEL[row.temperatureClass]}
                </span>
              ) : null}
            </div>
            {row.legend ? <div className="text-xs text-muted-foreground">{row.legend}</div> : null}
          </div>
          {countColumns.length > 0 ? (
            <div className="mt-3 grid grid-cols-2 gap-2 md:mt-0 md:contents">
              {countColumns.map((column) => (
                <CellLabel
                  key={column.key}
                  column={column}
                  cell={row.cells[column.key]}
                  name={`${row.label} ${column.inputLabel}`}
                  temperatureClass={row.temperatureClass}
                />
              ))}
            </div>
          ) : null}
        </div>

        {/* Sub-rows: the item's further count lines (grade bands, milk SKUs).
            They share the parent's single temperature, which is why they live
            inside the lines block rather than as sibling rows. */}
        {row.subRows?.map((sub) => (
          <div key={sub.key} className="mt-3 border-l pl-3 md:mt-2 md:border-l-0 md:pl-0">
            <div className="md:grid md:grid-cols-[var(--record-grid-cols)] md:items-center md:gap-4">
              <div className="min-w-0 text-xs text-muted-foreground md:pl-4">{sub.legend}</div>
              {countColumns.length > 0 ? (
                <div className="mt-2 grid grid-cols-2 gap-2 md:mt-0 md:contents">
                  {countColumns.map((column) => (
                    <CellLabel
                      key={column.key}
                      column={column}
                      cell={sub.cells[column.key]}
                      // The item name stays in the accessible name: a bare
                      // "PK3-5 served" is ambiguous across items on one card.
                      name={`${row.label} ${sub.legend} ${column.inputLabel}`}
                      temperatureClass={row.temperatureClass}
                    />
                  ))}
                </div>
              ) : null}
            </div>
            {sub.footer}
          </div>
        ))}

        {row.footer}
      </div>

      {tempColumns.length > 0 ? (
        <div
          className={cn(
            'mt-3 grid gap-2 md:mt-0 md:shrink-0 md:grid-cols-[var(--record-temp-cols)] md:items-center md:gap-4',
            tempColumns.length > 1 ? 'grid-cols-2' : 'grid-cols-1',
          )}>
          {tempColumns.map((column) => (
            <CellLabel
              key={column.key}
              column={column}
              cell={row.cells[column.key]}
              name={`${row.label} ${column.inputLabel}`}
              temperatureClass={row.temperatureClass}
            />
          ))}
        </div>
      ) : null}
    </li>
  )
}

/** One control under its own `md:hidden` mini-label — the chef's mobile stacking. */
function CellLabel({
  column,
  cell,
  name,
  temperatureClass,
}: {
  column: RecordEntryColumn
  cell: RecordEntryCell | undefined
  name: string
  temperatureClass: TemperatureClass | null
}) {
  // A row may legitimately skip a column — the milk group's parent row carries
  // the temperature but no count, its SKU sub-rows carry the counts. Hold the
  // desktop track with an empty cell and print NO mini-label, or a phone shows
  // a "Received" heading with nothing under it.
  if (!cell) {
    return <div aria-hidden />
  }
  return (
    <label className="flex min-w-0 flex-col gap-1 md:block">
      <span className="text-xs text-muted-foreground md:hidden">{column.header}</span>
      {cell.kind === 'count' ? (
        <CountCellView cell={cell} name={name} />
      ) : (
        <TempCellView cell={cell} name={name} temperatureClass={temperatureClass} />
      )}
    </label>
  )
}

/** The amber low-confidence ring, or a plain wrapper. Presentation only. */
function FlagWrap({ flag, className, children }: { flag?: 'check'; className?: string; children: ReactNode }) {
  return flag === 'check' ? (
    <div className={cn('rounded-md ring-2 ring-amber-500/60', className)} title={RECORD_ENTRY_CHECK_HINT}>
      {children}
    </div>
  ) : (
    <div className={className}>{children}</div>
  )
}

function CountCellView({ cell, name }: { cell: RecordEntryCountCell; name: string }) {
  return (
    <div className="flex items-center gap-1.5">
      {cell.tip}
      <FlagWrap flag={cell.flag} className="min-w-0 flex-1 md:flex-none">
        <CountInput
          className="w-full md:w-16"
          aria-label={name}
          value={cell.value}
          disabled={cell.disabled}
          onValueChange={cell.onValueChange}
        />
      </FlagWrap>
    </div>
  )
}

/**
 * The three states a temperature cell can be in, copied from the states
 * `TemperatureTable` renders (`temperature-table.tsx:174-228`) so the shared
 * page table and these entry tables never drift:
 *   not offered → the room-temp pill (the record proves it was considered);
 *   submitted   → the stored value behind a Lock tip, no input;
 *   edit        → `TempCheckpointInput` plus the cafeteria's Info tip.
 */
function TempCellView({
  cell,
  name,
  temperatureClass,
}: {
  cell: RecordEntryTempCell
  name: string
  temperatureClass: TemperatureClass | null
}) {
  if (!cell.offered) {
    return (
      <span className="inline-flex h-9 items-center rounded-md bg-muted px-3 text-sm text-muted-foreground">
        {NO_READING_LABEL}
      </span>
    )
  }
  if (cell.submitted) {
    const differs = cell.reading != null && cell.reading.value !== cell.submitted.value
    return (
      <span className="inline-flex items-center gap-1 tabular-nums">
        {formatTemperatureF(cell.submitted.value)}
        <CafeteriaModeTip
          kind="submitted"
          at={cell.submitted.at}
          byName={cell.submitted.byName}
          cafeteriaLabel={differs && cell.reading ? formatTemperatureF(cell.reading.value) : null}
        />
      </span>
    )
  }
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1.5">
        <FlagWrap flag={cell.flag} className="min-w-0 flex-1">
          <TempCheckpointInput
            value={cell.value}
            onChange={cell.onChange}
            ariaLabel={name}
            temperatureClass={tempCheckpointClass(temperatureClass)}
            disabled={cell.disabled}
          />
        </FlagWrap>
        {cell.reading ? (
          <CafeteriaModeTip
            kind="logged"
            label={formatTemperatureF(cell.reading.value)}
            loggedAt={cell.reading.loggedAt}
            loggedByName={cell.reading.loggedByName}
          />
        ) : null}
      </div>
      {temperatureCellInvalid(cell.value) ? <p className="text-xs text-destructive">{TEMPERATURE_RANGE_HINT}</p> : null}
    </div>
  )
}

/** The class maps `TemperatureTable` uses (`temperature-table.tsx:237-248`). */
function tempCheckpointClass(temperatureClass: TemperatureClass | null): TempCheckpointClass {
  switch (temperatureClass) {
    case 'HOT':
      return 'hot'
    case 'CHILLED':
      return 'chilled'
    case 'AMBIENT':
      return 'ambient'
    default:
      return null
  }
}

function SkeletonRows({ columnCount }: { columnCount: number }) {
  return (
    <>
      {Array.from({ length: RECORD_ENTRY_SKELETON_ROWS }, (_, i) => (
        <li key={i} className="px-3 py-3 md:flex md:items-center md:gap-4 md:px-4">
          <div className="min-w-0 md:flex-1">
            <Skeleton className="h-4 w-56 max-w-full" />
          </div>
          <div className="mt-3 flex gap-2 md:mt-0 md:shrink-0">
            {Array.from({ length: columnCount }, (_, j) => (
              <Skeleton key={j} className="h-9 w-full md:w-40" />
            ))}
          </div>
        </li>
      ))}
    </>
  )
}
